import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  LayoutDashboard, FileInput, FileOutput, Users, Download,
  Settings, LogOut, Menu, X, ChevronRight, Activity, Receipt, FileCheck, Landmark
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/angebote', label: 'Angebote', icon: FileCheck },
  { path: '/eingangsrechnungen', label: 'Eingangsrechnungen', icon: FileInput },
  { path: '/ausgangsrechnungen', label: 'Ausgangsrechnungen', icon: FileOutput },
  { path: '/umsatzsteuer', label: 'Umsatzsteuer', icon: Receipt },
  { path: '/steuererklaerung', label: 'Steuererklärung', icon: Landmark },
  { path: '/kontakte', label: 'Kontakte', icon: Users },
  { path: '/export', label: 'Export', icon: Download },
  { path: '/einstellungen', label: 'Einstellungen', icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-layout">
      
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 glass-card flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-6">
          <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center neon-glow">
            <Activity className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-lg text-foreground tracking-tight">
              LAIzy<span className="text-primary">Finance</span>
            </h1>
          </div>
          <button
            className="lg:hidden ml-auto text-muted-foreground hover:text-foreground"
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <Separator className="bg-border/50" />

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  isActive
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className="w-4.5 h-4.5 flex-shrink-0" />
              <span>{item.label}</span>
              <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </NavLink>
          ))}
        </nav>

        <Separator className="bg-border/50" />

        {/* User section */}
        <div className="px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'Benutzer'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email || ''}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-destructive text-sm"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-64 min-h-screen overflow-x-hidden">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 glass-card flex items-center px-4 lg:px-8 border-b border-border/30">
          <button
            className="lg:hidden mr-3 text-muted-foreground hover:text-foreground"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono-data">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="hidden sm:inline">System aktiv</span>
          </div>
        </header>

        {/* Page content */}
        <div className="p-3 sm:p-4 lg:p-8 max-w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
